import React from "react";
import Banner from "../../assets/banner-image.jpg"
import { ArrowRight, Shield, Code, Smartphone, RefreshCw } from "lucide-react";

const WhatWeDo = () => {
  // Enhanced service data with images
  const services = [
    {
      icon: <Code className="w-6 h-6 text-blue-600" />,
      title: "Software Development",
      description:
        "Custom software solutions tailored to your business needs with cutting-edge technologies and best practices.",
      altText: "Software Development",
    },
    {
      icon: <RefreshCw className="w-6 h-6 text-purple-600" />,
      title: "Digital Transformation",
      description:
        "Modernize your business processes and leverage technology to stay competitive in the digital landscape.",
      altText: "Digital Transformation",
    },
    {
      icon: <Smartphone className="w-6 h-6 text-green-600" />,
      title: "Mobile Applications",
      description:
        "Native and cross-platform mobile applications that deliver exceptional user experiences across devices.",
      altText: "Mobile Applications",
    },
    {
      icon: <Shield className="w-6 h-6 text-red-600" />,
      title: "Cybersecurity",
      description: "Protect your digital assets with comprehensive security solutions and proactive threat management.",
      altText: "Cybersecurity",
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden bg-white">
      {/* Abstract Tech Pattern Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-full h-full opacity-10">
          {/* Circuit-like lines */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-blue-500 via-transparent to-blue-500"></div>
          <div className="absolute top-2/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
          <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-blue-500 via-transparent to-blue-500"></div>

          {/* Vertical lines */}
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-blue-500 to-transparent"></div>
          <div className="absolute top-0 left-2/4 w-px h-full bg-gradient-to-b from-blue-500 via-transparent to-blue-500"></div>
          <div className="absolute top-0 left-3/4 w-px h-full bg-gradient-to-b from-transparent via-purple-500 to-transparent"></div>

          {/* Floating dots */}
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-blue-600"
              style={{
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.4 + 0.1,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with animated underline */}
        <div className="text-left mb-16 relative">
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 mb-6">
            What We Do
          </h2>

          <p className="text-gray-600  text-lg">
            We accelerate digital innovation with cutting-edge technology solutions tailored to your business needs.
          </p>
        </div>

        {/* Services Section with Cards */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 border border-gray-100 flex flex-col h-full transform hover:-translate-y-1"
            >

              <div className="p-6 flex-grow flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-lg bg-gray-50 group-hover:bg-blue-50 transition-colors duration-300">
                    {service.icon}
                  </div>
                  <h3 className="ml-3 text-xl font-bold text-gray-900">{service.title}</h3>
                </div>

                <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>

                <div className="mt-auto">
                  <button className="flex items-center text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                    <span>Learn more</span>
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Features Section */}
        <div className="mt-24">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 md:p-12 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Ready to Accelerate Your Digital Journey?
                </h3>
                <p className="text-gray-600 mb-6">
                  Our team of experts is ready to help you navigate the complexities of digital transformation and deliver solutions that drive real business value.
                </p>
                <ul className="space-y-3 mb-8">
                  {['Agile development methodology', 'Expert technology consultants', 'Dedicated support team', 'Industry-specific solutions'].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-1">
                        <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                      </div>
                      <span className="ml-3 text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                {/* <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg flex items-center">
                  <span>Schedule a Consultation</span>
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button> */}
              </div>

              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src={Banner}
                    alt="Technology team collaboration"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-transparent"></div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-purple-200 rounded-full opacity-70 blur-xl"></div>
                <div className="absolute -left-10 -top-10 w-32 h-32 bg-blue-200 rounded-full opacity-70 blur-xl"></div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default WhatWeDo;